/**
 * Image Generation Service
 * Uses Gemini 3 Pro Image Preview (Nano Banana Pro) to generate brand-aligned images
 * Based on: https://ai.google.dev/gemini-api/docs/image-generation
 */

import { auth } from '../config/firebase';
import type { BrandBook, ImageGenContext } from '../types/brandBook';
import type { CustomerIntelligence } from '../types/customerIntelligence';

export interface GeneratedImage {
  url: string; // Public Storage URL
  prompt: string;
  style: string;
}

/**
 * Image template types for different use cases
 */
export type ImageTemplate = 
  | 'vehicle-hero'    // Fahrzeug-Hero für Landingpages
  | 'showroom'        // Showroom/Autohaus-Ambiente
  | 'team'            // Team-Fotos
  | 'service'         // Werkstatt/Service
  | 'promo'           // Sonderaktionen/Sales
  | 'lifestyle'       // Lifestyle-Bilder mit Fahrzeugen
  | 'custom';         // Benutzerdefiniert

/**
 * Aspect Ratio type for cross-channel ads
 */
export type AspectRatio = '1:1' | '9:16' | '1.91:1' | '4:5' | '1.2:1';

/**
 * Template configurations for Autohaus
 */
const IMAGE_TEMPLATES: Record<ImageTemplate, {
  label: string;
  description: string;
  basePrompt: string;
  style: string;
}> = {
  'vehicle-hero': {
    label: 'Fahrzeug Hero',
    description: 'Professionelles Fahrzeugfoto für Landingpages',
    basePrompt: 'Professional car photography, hero shot, modern vehicle in showroom or outdoor setting, dramatic lighting, high-end automotive advertisement style',
    style: 'hero'
  },
  'showroom': {
    label: 'Showroom',
    description: 'Autohaus-Innenansicht mit Fahrzeugen',
    basePrompt: 'Modern car dealership showroom interior, luxury vehicles on display, bright professional lighting, clean architecture, premium automotive retail space',
    style: 'showroom'
  },
  'team': {
    label: 'Team',
    description: 'Professionelle Team-Fotos',
    basePrompt: 'Professional automotive sales team, friendly staff in dealership, business casual attire, welcoming atmosphere, customer service focus',
    style: 'team'
  },
  'service': {
    label: 'Service & Werkstatt',
    description: 'Werkstatt und Service-Bereich',
    basePrompt: 'Modern car service center, professional mechanics working, clean workshop environment, automotive repair and maintenance, high-tech diagnostic equipment',
    style: 'service'
  },
  'promo': {
    label: 'Sonderaktion',
    description: 'Bilder für Sales und Aktionen',
    basePrompt: 'Automotive sales promotion, special offer visual, exciting car deal, dynamic composition, attention-grabbing marketing image',
    style: 'promo'
  },
  'lifestyle': {
    label: 'Lifestyle',
    description: 'Fahrzeuge im Lifestyle-Kontext',
    basePrompt: 'Lifestyle automotive photography, car in scenic environment, family or adventure context, aspirational imagery, emotional connection',
    style: 'lifestyle'
  },
  'custom': {
    label: 'Benutzerdefiniert',
    description: 'Eigener Prompt eingeben',
    basePrompt: '',
    style: 'custom'
  }
};

export const imageGenerationService = {
  /**
   * Generate a set of brand images based on company profile using Cloud Function
   * Uses Nano Banana Pro (Gemini 3 Pro Image Preview) for highest quality
   */
  async generateBrandImages(context: {
    companyName: string;
    industry: string;
    brandTone: 'formal' | 'casual';
    colors?: string[];
    keywords: string[];
    referenceImageUrl?: string;
    aspectRatio?: AspectRatio; // NEW: Multi-format support
  }): Promise<GeneratedImage[]> {
    try {
      console.log('🎨 Calling Nano Banana Pro for Image Generation...');
      console.log('Context:', { 
        companyName: context.companyName, 
        industry: context.industry,
        keywords: context.keywords?.slice(0, 3),
        hasReferenceImage: !!context.referenceImageUrl,
        aspectRatio: context.aspectRatio || '1:1'
      });
      
      const token = await auth.currentUser?.getIdToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const requestBody = {
        data: {
          companyName: context.companyName,
          industry: context.industry,
          brandTone: context.brandTone,
          keywords: context.keywords || [],
          colors: context.colors || [], // Include brand colors for image generation
          referenceImageUrl: context.referenceImageUrl,
          aspectRatio: context.aspectRatio || '1:1' // Default to Square
        }
      };
      
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch('https://us-central1-aideline-pro.cloudfunctions.net/generateBrandImages', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('📦 Response result:', result);
      
      const images = result.data?.images || [];
      
      if (images.length === 0) {
        console.warn('⚠️ No images returned from Cloud Function');
      } else {
        console.log(`✅ Received ${images.length} generated images:`);
        images.forEach((img: GeneratedImage, i: number) => {
          console.log(`  ${i + 1}. ${img.style}: ${img.url}`);
        });
      }
      
      return images;
      
    } catch (error) {
      console.error('❌ Failed to generate images:', error);
      // Return empty array instead of throwing - allows UI to handle gracefully
      return [];
    }
  },

  /**
   * Generate images using Brand Book context with Nano Banana Pro
   * Optimized for Autohaus/Automotive use cases
   */
  async generateBrandBookImage(
    brandBook: BrandBook,
    template: ImageTemplate,
    customPrompt?: string,
    vehicleType?: string,
    referenceImageUrl?: string
  ): Promise<GeneratedImage[]> {
    try {
      console.log('🎨 Generating Brand Book image with Nano Banana Pro...');
      console.log('Template:', template);
      console.log('Brand:', brandBook.businessName);
      
      // Build enhanced prompt from Brand Book data
      const templateConfig = IMAGE_TEMPLATES[template];
      const basePrompt = template === 'custom' && customPrompt 
        ? customPrompt 
        : templateConfig.basePrompt;
      
      // Enhance prompt with brand context
      const brandEnhancements: string[] = [];
      
      // Add brand colors
      if (brandBook.brandColors.length > 0) {
        brandEnhancements.push(`brand colors: ${brandBook.brandColors.slice(0, 3).join(', ')}`);
      }
      
      // Add visual style
      if (brandBook.visualStyle) {
        brandEnhancements.push(`${brandBook.visualStyle} style`);
      }
      
      // Add image style preferences
      if (brandBook.imageStyle) {
        if (brandBook.imageStyle.colorMood) {
          brandEnhancements.push(`${brandBook.imageStyle.colorMood} color mood`);
        }
        if (brandBook.imageStyle.photography) {
          brandEnhancements.push(`${brandBook.imageStyle.photography} photography style`);
        }
      }
      
      // Add vehicle type if specified
      if (vehicleType) {
        brandEnhancements.push(`featuring ${vehicleType}`);
      }
      
      // Add brand tone
      if (brandBook.voiceAnalysis?.formality === 'formal') {
        brandEnhancements.push('premium, professional atmosphere');
      } else {
        brandEnhancements.push('friendly, approachable atmosphere');
      }
      
      // Build final prompt
      const enhancedPrompt = `${basePrompt}, ${brandEnhancements.join(', ')}, for ${brandBook.businessName}`;
      
      console.log('📝 Enhanced prompt:', enhancedPrompt);
      
      // Use existing generateBrandImages with enhanced context
      const images = await this.generateBrandImages({
        companyName: brandBook.businessName,
        industry: brandBook.industry,
        brandTone: brandBook.voiceAnalysis?.formality === 'formal' ? 'formal' : 'casual',
        colors: brandBook.brandColors,
        keywords: [
          templateConfig.style,
          vehicleType || 'vehicle',
          ...brandBook.coreKeywords.slice(0, 3)
        ],
        referenceImageUrl
      });
      
      // Add the enhanced prompt to the results
      return images.map(img => ({
        ...img,
        prompt: enhancedPrompt
      }));
      
    } catch (error) {
      console.error('❌ Failed to generate Brand Book image:', error);
      return [];
    }
  },

  /**
   * Get available image templates
   */
  getImageTemplates(): Array<{
    id: ImageTemplate;
    label: string;
    description: string;
  }> {
    return Object.entries(IMAGE_TEMPLATES).map(([id, config]) => ({
      id: id as ImageTemplate,
      label: config.label,
      description: config.description
    }));
  },

  /**
   * Build image context from Brand Book
   */
  getImageContextFromBrandBook(brandBook: BrandBook): ImageGenContext {
    return {
      brandColors: brandBook.brandColors,
      primaryColor: brandBook.primaryColor,
      visualStyle: brandBook.visualStyle,
      imageStyle: brandBook.imageStyle,
      industry: brandBook.industry,
      businessName: brandBook.businessName
    };
  },

  /**
   * Build image context from Customer Intelligence (Master Record)
   */
  getImageContextFromCI(ci: CustomerIntelligence): ImageGenContext {
    return {
      brandColors: ci.visual.brandColors,
      primaryColor: ci.visual.primaryColor,
      visualStyle: ci.visual.visualStyle,
      imageStyle: ci.visual.imageStyle || {
        photography: 'lifestyle',
        colorMood: 'neutral',
        subjects: []
      },
      industry: ci.brand.industry,
      businessName: ci.businessName
    };
  },

  /**
   * Generate images using CI context (360 View) with specific aspect ratio
   */
  async generateImagesFromCI(
    ci: CustomerIntelligence,
    template: ImageTemplate,
    options?: {
      customPrompt?: string;
      vehicleType?: string;
      aspectRatio?: AspectRatio;
    }
  ): Promise<GeneratedImage[]> {
    const templateConfig = IMAGE_TEMPLATES[template];
    const basePrompt = template === 'custom' && options?.customPrompt 
      ? options.customPrompt 
      : templateConfig.basePrompt;
    
    // Add brand context from CI Master Record
    const brandEnhancements: string[] = [];
    if (ci.visual.brandColors.length > 0) {
      brandEnhancements.push(`brand colors: ${ci.visual.brandColors.slice(0, 3).join(', ')}`);
    }
    if (ci.visual.visualStyle) {
      brandEnhancements.push(`${ci.visual.visualStyle} style`);
    }
    if (ci.voice.formality === 'formal') {
      brandEnhancements.push('premium, professional atmosphere');
    }

    // Add product context if automotive
    if (ci.brand.industry.toLowerCase().includes('auto') && !options?.vehicleType && ci.brand.offerings.length > 0) {
      brandEnhancements.push(`featuring ${ci.brand.offerings[0]}`);
    }

    const enhancedPrompt = `${basePrompt}, ${brandEnhancements.join(', ')}, for ${ci.businessName}`;

    return this.generateBrandImages({
      companyName: ci.businessName,
      industry: ci.brand.industry,
      brandTone: ci.voice.formality === 'formal' ? 'formal' : 'casual',
      colors: ci.visual.brandColors,
      keywords: [
        templateConfig.style,
        options?.vehicleType || 'service',
        ...ci.keywords.core.slice(0, 3)
      ],
      referenceImageUrl: ci.visual.brandImages?.[0],
      aspectRatio: options?.aspectRatio || '1:1'
    });
  }
};
